import { useCallback } from 'react';
import { calendarEventsAPI } from 'api';
import { CalendarEventForm, DownloadCalendaForm, CalendarEventFormData, CalendarEventTypes, CalendarProviders } from 'features/calendar-events/types';
import { UserTypes } from 'features/types';
import { ROUTES } from 'app/routes';
import axios from "axios";

export async function openAuthPopupAndWait(): Promise<string> {
  return new Promise((resolve) => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const url = ROUTES.GOOGLE_CALENDAR_AUTH;
    const popup = window.open(url, 'googleAuthPopup', `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`);
    if (!popup) {
      alert("Popup blocked — please allow popups for this site.");
      return resolve('popup_blocked');
    }

    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        resolve('user_canceled');
      }
    }, 500);

    window.addEventListener("message", (event) => {
      if (event.data === "google-auth-success" || event.data === "get-auth-url-failed") {
        clearInterval(timer);
        popup.close();
        resolve(event.data);
      }
    });
  });
}

const useCalendarEvent = () => {
  const addEvent = useCallback(
    async <T extends object>(eventData: CalendarEventForm<T>, provider: CalendarProviders, eventType: CalendarEventTypes, userType: UserTypes): Promise<CalendarEventFormData> => {
      let isInvalidGrant = false;

      for (let i = 0; i < 2; i++) {
        if (isInvalidGrant) continue;
        try {
          if (userType === UserTypes.JobSeeker) {
            const response = await calendarEventsAPI.createGoogleCalendarEventForJobSeeker<T>(eventData);
            return response;
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            const errMessage = error.response?.data?.error;
            if (errMessage?.includes('id_exists')) return { status: false, message: 'id_exists' };
            isInvalidGrant = errMessage?.includes('invalid_request') || errMessage?.includes('invalid_grant');
          }
        }

        if (isInvalidGrant) {
          const result = await openAuthPopupAndWait();
          if (result === 'google-auth-success') {
            isInvalidGrant = false;
          }
          if (!result) throw new Error('user_canceled');
        } else {
          break;
        }
      }
      throw new Error('Failed to create calendar event after retry');
    }, []);

  const downloadCalendar = useCallback(
    async (downloadData: DownloadCalendaForm, provider: CalendarProviders, eventType: CalendarEventTypes, userType: UserTypes) => {
      let isInvalidGrant = false;

      for (let i = 0; i < 2; i++) {
        if (isInvalidGrant) continue;
        try {
          if (userType === UserTypes.JobSeeker) {
            return await calendarEventsAPI.downloadGoogleCalendarEventsForJobSeeker(downloadData);
          } else {
            throw new Error(`Unknown userType: ${userType}`);
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            const errMessage = error.response?.data?.error;
            isInvalidGrant = errMessage?.includes('invalid_request') || errMessage?.includes('invalid_grant');
          }
        }

        if (isInvalidGrant) {
          const result = await openAuthPopupAndWait();
          if (result === 'google-auth-success') {
            isInvalidGrant = false;
          }
          if (!result) throw new Error('user_canceled');
        } else {
          break;
        }
      }
      throw new Error('Failed to download calendar events after retry');
    }, []);

  return { addEvent, downloadCalendar };
};

export default useCalendarEvent;